import { Camera, Settings, Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** Returns true only when the browser/WebView reports camera access as denied. */
export async function isCameraPermissionDenied(): Promise<boolean> {
  try {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) return false;
    const status = await navigator.permissions.query({ name: "camera" as PermissionName });
    return status.state === "denied";
  } catch {
    return false;
  }
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadInstead: () => void;
  onTryAgain: () => void;
}

export function CameraPermissionHelp({ open, onOpenChange, onUploadInstead, onTryAgain }: Props) {
  const { t } = useTranslation();
  const ios = isIOS();
  const steps = ios
    ? [
        t("input.cameraHelpStepIos1"),
        t("input.cameraHelpStepIos2"),
        t("input.cameraHelpStepIos3"),
        t("input.cameraHelpStepIos4"),
      ]
    : [
        t("input.cameraHelpStep1"),
        t("input.cameraHelpStep2"),
        t("input.cameraHelpStep3"),
        t("input.cameraHelpStep4"),
      ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="mx-auto mb-2 size-14 rounded-full bg-muted flex items-center justify-center">
            <Camera className="size-6 text-muted-foreground" />
          </div>
          <DialogTitle className="text-center">{t("input.cameraHelpTitle")}</DialogTitle>
          <DialogDescription className="text-center">
            {t("input.cameraHelpBody")}
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-2 text-sm">
          {steps.map((s, i) => (
            <li key={s} className="flex gap-3">
              <span className="size-6 shrink-0 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <span className="pt-0.5">{s}</span>
            </li>
          ))}
        </ol>
        <div className="flex flex-col gap-2 pt-2">
          <Button className="min-h-11" onClick={onTryAgain}>
            <Settings className="size-4" /> {t("input.cameraHelpTryAgain")}
          </Button>
          <Button variant="outline" className="min-h-11" onClick={onUploadInstead}>
            <Upload className="size-4" /> {t("input.cameraHelpUploadInstead")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
